import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';

import { UntypedFormControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { translateObject, TranslocoService } from '@jsverse/transloco';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';

import { Constants, FieldConstants } from 'app/constants/constants';
import { FieldResult } from 'app/modules/field/field-create-modal/field-create-modal.component';
import { SelectedFilterType } from 'app/modules/field/field-list-filter/field-list-filter.component';
import { AlertService } from 'app/services/alert.service';
import { EnableClassTelemetry } from 'app/services/otel.service';
import { WrappedRequest } from '../api.service';
import {
  RealtimeConnection,
  RealtimeEventHandler,
  RealtimeMessage,
  RealtimeService,
} from '../realtime.service';
import { GroupService } from '../semantria-api/group.service';
import { StorageAPIService, StorageListResult } from '../storage-api.service';
import { FieldBucket } from '../../modules/field/field-buckets/field-buckets.component';
import { SystemService } from '../../services/system.service';

export interface FieldDetails {
  groupId: string;
  projectId: string;
  projectName: string;
  collectionId: string;
  collectionName: string;
  documentCount: number;
}

export interface XIFieldLabelData {
  [key: string]: string;
}
export interface XIFieldData {
  id: string;
  label: {
    locales: XIFieldLabelData;
  };
  default: string;
}
export interface SideloadedMetadataLabels {
  xiFieldLabels?: Array<XIFieldData>;
  xiFieldOptionLabels?: Array<XIFieldData>;
}

export interface Field {
  details: FieldDetails[];
  id: string;
  fieldName: string;
  fieldType: FieldType;
  groupId: string;
  label: string;
  description: string;
  createdAt: string | Date;
  modifiedAt: string | Date;
  documentCount: number;
  normalizedName: string;
  externalId?: string;
  metadata?: FieldMetadata;
  alias?: boolean;
  aliasedFields?: string[];
  locked?: boolean;
  sideloaded?: SideloadedMetadataLabels;
  buckets?: FieldBucket[];
  identityField?: boolean;
  journeyGroupUsages?: JourneyGroupUsage[];
}

interface JourneyGroupUsage {
  id: string;
  analysisGroupId: string;
  analysisGroupName: string;
  projectId: string;
  usedAsDefaultField: boolean;
  usedAsResolverField: boolean;
}

export type FieldType =
  | 'alias'
  | 'calculated'
  | 'keyword'
  | 'long'
  | 'date'
  | 'double'
  | 'rating'
  | 'weight';

export interface FieldMetadata {
  filterable?: boolean;
  redacted?: boolean;
  weight?: boolean;
  calculationType?: string;
  calculationDefinition?: object;
  display?: boolean;
  [key: string]: any;
  calculationFieldType?: string;
}

export interface CustomField {
  id: string;
  label: string;
  description: string;
  fieldName: string;
  fieldType: string;
  isFilterable: boolean;
  aliasedFields?: string[];
  isDisplayed: boolean;
  calculationFieldType?: string;
}

export interface CreateFieldBulkData {
  label: string;
  fieldType: string;
  description: string;
  metadata: { [key: string]: any };
}

export interface CreateFieldBulkResult {
  items: CreateFieldBulkResultItem[];
}

export interface CreateFieldBulkResultItem {
  field: Field;
  status: string;
  error: string;
}

export type FieldSort = 'auto';
export type FieldFilter = '';

export enum FieldRedactionBehavior {
  HIDE = 'HIDE',
  MASK = 'MASK',
}

export interface FieldTypeOption {
  value: string;
  label: string;
  detail: string;
}

interface StandardField {
  property: string;
  label: string;
  type: 'string' | 'date' | 'number' | 'url';
  pipeParams?: string;
}

export enum SortTypes {
  DATE = 1,
  NAME = 2,
  DOC_COUNT = 3,
}

@EnableClassTelemetry()
@Injectable({
  providedIn: 'root',
})
export class FieldService extends StorageAPIService implements OnDestroy {
  private static cache: { [key: string]: StorageListResult<Field> } = {};
  private static fieldMapCache: { [key: string]: FieldMap } = {};
  private static groupId: string;

  private changeEvent = new BehaviorSubject<StorageListResult<Field>>(null);
  private changeEvent$ = this.changeEvent.asObservable();
  private selectedGroupSubscription: Subscription;
  private fieldsRequest: WrappedRequest<StorageListResult<Field>>;

  private realtimeHandlers: RealtimeConnection[] = [];
  private realtimeEventSource = new Subject<RealtimeMessage<Field>>();
  public realtimeEvent$ = this.realtimeEventSource.asObservable();

  private selectedFilterOptions: SelectedFilterType[] = [];

  static readonly FIELD_VALID_RE = /^[.a-z0-9_ -]+$/i;
  static readonly GLOBAL_FIELD_VALID_RE = /^[.a-z0-9_ :\-]+$/i;
  static readonly FIELD_VALID_CUSTOM_RE = /^(?!a_|cl_).*/i;
  static readonly NORMALIZED_NAME_RE = /[^a-z0-9]/i;
  static readonly STANDARD_FIELDS: StandardField[] = [
    { property: 'id', label: 'ID', type: 'string' },
    { property: 'indexedAt', label: 'Indexed', type: 'date' },
    { property: 'createdAt', label: 'Created', type: 'date' },
    { property: 'language', label: 'Language', type: 'string' },
    { property: 'creator', label: 'Creator', type: 'string' },
    { property: 'url', label: 'URL', type: 'url' },
    { property: 'source', label: 'Source', type: 'string' },
    { property: 'parentId', label: 'Parent ID', type: 'string' },
    { property: 'locationId', label: 'Location ID', type: 'string' },
    { property: 'publisher', label: 'Publisher', type: 'string' },
    { property: 'type', label: 'Type', type: 'string' },
  ];
  static readonly STANDARD_FIELDS_LABEL_TO_ID = {};
  static readonly STANDARD_FIELDS_ID_TO_LABEL = {};

  static readonly SOCIAL_FIELDS: CustomField[] = [
    {
      id: 'sc_Place',
      label: 'Place',
      description: 'Place',
      fieldName: 'metadata.sc_Place',
      fieldType: 'string',
      isFilterable: true,
      isDisplayed: true,
    },
    {
      id: 'sc_sourceType',
      label: 'Source Type',
      description: 'Source Type',
      fieldName: 'metadata.sc_sourceType',
      fieldType: 'string',
      isFilterable: true,
      isDisplayed: true,
    },
    {
      id: 'sc_domain',
      label: 'Domain',
      description: 'Domain',
      fieldName: 'metadata.sc_domain',
      fieldType: 'string',
      isFilterable: true,
      isDisplayed: true,
    },
    {
      id: 'sc_author',
      label: 'Author',
      description: 'Author',
      fieldName: 'metadata.sc_author',
      fieldType: 'string',
      isFilterable: true,
      isDisplayed: true,
    },
  ];

  static readonly defaultSideLoadForMetaDatas = ['xiFieldLabels', 'xiFieldOptionLabels'];

  constructor(
    http: HttpClient,
    systemService: SystemService,
    private groupService: GroupService,
    private realtimeService: RealtimeService,
    private alertService: AlertService,
    private translocoService: TranslocoService
  ) {
    super(http, systemService);
    this.selectedGroupSubscription = this.groupService.selectedGroup$.subscribe((group) => {
      this.getFieldsForGroup(group?.group_id, true);
      this.subscribeToRealtimeHandlers(group?.group_id);
    });
    for (const standardField of FieldService.STANDARD_FIELDS) {
      FieldService.STANDARD_FIELDS_LABEL_TO_ID[standardField.label] = standardField.property;
      FieldService.STANDARD_FIELDS_ID_TO_LABEL[standardField.property] = standardField.label;
    }
  }

  static isStandardField(fieldName: string): boolean {
    return this.STANDARD_FIELDS.filter((sf) => sf.property === fieldName).length > 0;
  }

  static isSectionProperty(fieldName: string): boolean {
    return fieldName.startsWith(Constants.SECTION_PROPERTIES_PREFIX);
  }

  public static isNumericField(field?: Field): boolean {
    return this.hasNumerics(field?.fieldType, field?.metadata?.calculationFieldType);
  }

  public static hasNumerics(...fieldTypes: (string | undefined | null)[]): boolean {
    return fieldTypes.some((type) => type === 'long' || type === 'double');
  }

  public static hasDate(...fieldTypes: (string | undefined | null)[]): boolean {
    return fieldTypes.some((type) => type === 'date');
  }

  public static hasKeyword(...fieldTypes: (string | undefined | null)[]): boolean {
    return fieldTypes.some((type) => type === 'keyword');
  }

  ngOnDestroy(): void {
    this.selectedGroupSubscription?.unsubscribe();
    this.unsubscribeFromRealtimeHandlers();
  }

  public on(namespace: string, fn: RealtimeEventHandler<Field>): RealtimeConnection {
    return this.realtimeService.on(
      namespace,
      {
        field: (data: RealtimeMessage<Field>) => {
          FieldService.map(data.object);
          fn(data);
        },
      },
      'fieldService'
    );
  }

  private subscribeToRealtimeHandlers(groupId: string) {
    this.unsubscribeFromRealtimeHandlers();
    if (!groupId) {
      return;
    }
    this.realtimeHandlers = [this.on(groupId, this.handleFieldEvent.bind(this))];
  }

  private unsubscribeFromRealtimeHandlers() {
    this.realtimeHandlers.forEach((handler) => {
      if (handler) {
        handler.disconnect();
      }
    });
  }

  private handleFieldEvent(data: RealtimeMessage<Field>) {
    if (data.verb === 'CREATE') {
      const groupId = this.groupService.getSelectedGroupId();
      this.getFieldsForGroup(groupId, true);
    }
    this.realtimeEventSource.next(data);
  }

  public static map(item: Field): Field {
    if (item.createdAt) {
      item.createdAt = new Date(item.createdAt);
    }
    if (item.modifiedAt) {
      item.modifiedAt = new Date(item.modifiedAt);
    }
    return item;
  }

  public static mapWithSideloaded(item: Field, sideloadedData: SideloadedMetadataLabels): Field {
    const sideloaded = {};
    const xiFieldLabels = sideloadedData?.xiFieldLabels?.filter(
      (label) => label.id === item.metadata['dataFieldId']?.toString()
    );
    const xiFieldOptionLabels = sideloadedData?.xiFieldOptionLabels?.filter(
      (label) => label.id === item.metadata['dataFieldId']?.toString()
    );
    sideloaded['xiFieldLabels'] = xiFieldLabels;
    sideloaded['xiFieldOptionLabels'] = xiFieldOptionLabels;
    return { ...FieldService.map(item), sideloaded };
  }

  public onChange() {
    return this.changeEvent$;
  }

  public async getFields(force: boolean = false): Promise<StorageListResult<Field>> {
    if (!this.groupService.getSelectedGroup()) {
      await this.groupService.refreshAndSelectActiveGroup();
    }
    const groupId = this.groupService.getSelectedGroupId();
    if (FieldService.cache[groupId] && !force) {
      FieldService.groupId = groupId;
      return FieldService.cache[groupId];
    }
    return this.getFieldsForGroup(groupId, force);
  }

  private async getFieldsForGroup(
    groupId: string,
    force = false
  ): Promise<StorageListResult<Field>> {
    if (!groupId) {
      return { count: 0, items: [] };
    }

    if (FieldService.groupId !== groupId || force) {
      FieldService.groupId = groupId;
      const fieldsPromise = this.searchFields(
        null,
        null,
        'auto',
        StorageAPIService.MAX_LIMIT,
        groupId
      );
      this.fieldsRequest = new WrappedRequest(fieldsPromise);
    }

    const result = await this.fieldsRequest.request;

    FieldService.cache[groupId] = result;
    FieldService.fieldMapCache[groupId] = createFieldMap(result);
    return result;
  }

  public async searchFields(
    keyword?: string,
    filter?: FieldFilter,
    _sort: FieldSort = 'auto',
    limit: number = 20,
    group_id: string = ''
  ): Promise<StorageListResult<Field>> {
    if (!this.groupService.getSelectedGroup()) {
      await this.groupService.refreshAndSelectActiveGroup();
    }
    const groupId = group_id || this.groupService.getSelectedGroupId();
    let queryString = '*';
    if (keyword) {
      queryString = keyword.replace(/[^a-zA-Z0-9_\-\*]/g, '');
      queryString = queryString.indexOf('*') >= 0 ? queryString : `*${queryString}*`;
    }

    const query = `label:${queryString}`;

    const result = await this.getPage<Field>(`/groups/${groupId}/fields`, {
      query,
      'page-limit': limit,
      ...this.getDefaultSideLoadForMetadatas(),
    });

    result.items = result.items.map((value) =>
      FieldService.mapWithSideloaded(value, result.sideloaded)
    );
    return result;
  }

  public async getFieldById(fieldId: string, sideLoad = true): Promise<Field> {
    return this.get<Field>(
      `/fields/${fieldId}`,
      sideLoad ? this.getDefaultSideLoadForMetadatas() : {}
    );
  }

  public async createField(
    groupId: string,
    label: string,
    fieldType: string,
    description?: string,
    metadata?: { [key: string]: any },
    alias?: boolean,
    aliasedFields?: string[]
  ): Promise<Field> {
    const result = await this.post<Field>(`/groups/${groupId}/fields`, {
      label,
      fieldType,
      metadata,
      description: description || label,
      alias,
      aliasedFields,
    });
    const fields = await this.getFields(true);
    this.changeEvent.next(fields);
    return result;
  }

  public async createFields(
    groupId: string,
    createFieldBulkDatas: CreateFieldBulkData[]
  ): Promise<CreateFieldBulkResult> {
    const result = await this.post<CreateFieldBulkResult>(
      `/groups/${groupId}/fields/_bulk`,
      createFieldBulkDatas
    );
    const fields = await this.getFields(true);
    this.changeEvent.next(fields);
    return result;
  }

  public async updateField(
    id: string,
    label: string,
    description: string,
    fieldType: string,
    metadata: { [key: string]: any },
    alias?: boolean,
    aliasedFields?: string[],
    buckets?: FieldBucket[],
    identityField?: boolean
  ): Promise<Field> {
    const result = await this.put<Field>(`/fields/${id}`, {
      label,
      description,
      fieldType,
      metadata,
      alias,
      aliasedFields,
      buckets,
      identityField,
    });
    const fields = await this.getFields(true);
    this.changeEvent.next(fields);
    return result;
  }

  public async deleteField(id: string): Promise<void> {
    await this.delete(`/fields/${id}`);
    const fields = await this.getFields(true);
    this.changeEvent.next(fields);
  }

  getUnusedFields(groupId: string): Promise<Field[]> {
    return this.get(`/groups/${groupId}/fields/_unused`);
  }

  getProjectFields(groupId: string, projects: string[]): Promise<StorageListResult<string>> {
    return this.get(`/groups/${groupId}/fields/_references`, { projects: projects.join() });
  }

  async deleteUnusedFields(groupId: string): Promise<Field[]> {
    const deletedFields = await this.delete<Field[]>(`/groups/${groupId}/fields/_unused`);
    const fields = await this.getFields(true);
    this.changeEvent.next(fields);
    return deletedFields;
  }

  async lockField(id: string): Promise<void> {
    const groupId = this.groupService.getSelectedGroupId();
    const result = await this.post<void>(`/groups/${groupId}/fields/${id}/_lock`, {});
    const fields = await this.getFields(true);
    this.changeEvent.next(fields);
    return result;
  }

  unlockField(id: string): Promise<void> {
    const groupId = this.groupService.getSelectedGroupId();
    return this.post<void>(`/groups/${groupId}/fields/${id}/_unlock`, {});
  }

  public static getField(s: string): Field {
    if (!this.fieldMapCache[this.groupId]) {
      return null;
    }
    return this.fieldMapCache[this.groupId][s];
  }

  public static getFieldShortName(s: string): string {
    return this.getField(s)?.label || s;
  }

  public static getFieldLongName(s: string): string {
    return this.getField(s)?.description || s;
  }

  public static getFieldType(s: string): string {
    return this.getField(s)?.fieldType;
  }

  public static getAliasFields(): Field[] {
    if (!this.fieldMapCache[this.groupId]) {
      return [];
    }

    return Object.values(this.fieldMapCache[this.groupId]).filter((f) => f.alias);
  }

  public static getSectionPropertyShortName(s: string): string {
    return s.replace(Constants.SECTION_PROPERTIES_PREFIX, '');
  }

  public static getSectionPropertyType(s: string): string {
    return Constants.SECTION_PROPERTY_TYPES[s];
  }

  public static isNumeric(s: string): boolean {
    const field = this.getField(s);
    return this.isNumericField(field);
  }

  public static isRating(s: string): boolean {
    return !!this.getField(s)?.metadata?.rating;
  }

  public static isVisible(s: string) {
    return this.getField(s)?.metadata?.display !== false;
  }

  public getFieldTypeOptions(): FieldTypeOption[] {
    const t = translateObject('field-service');
    return [
      {
        value: 'keyword',
        label: t['keyword'],
        detail: t['keywords-are-used-for'],
      },
      {
        value: 'rating',
        label: t['rating'],
        detail: t['ratings-are-answers-to'],
      },
      {
        value: 'long',
        label: t['integer'],
        detail: t['integers-are-numbers-that'],
      },
      {
        value: 'double',
        label: t['float'],
        detail: t['floating-point-numbers-are-used-for'],
      },
      {
        value: 'weight',
        label: t['weight'],
        detail: t['weights-are-applied-when'],
      },
      { value: 'date', label: t['date'], detail: t['dates-can-be-used-for'] },
    ];
  }

  public static clearCache(): void {
    this.cache = {};
    this.fieldMapCache = {};
    this.groupId = undefined;
  }

  static isCustomField(fieldName: string) {
    if (!fieldName) {
      return false;
    }
    const fName = fieldName.toLowerCase();
    return (
      fName.startsWith(FieldConstants.MD_) ||
      fName.startsWith(FieldConstants.A_) ||
      fName.startsWith(FieldConstants.CL_)
    );
  }

  public sortFields(filteredFields: Field[], currentSort = SortTypes.NAME, reverseSort = false) {
    switch (currentSort) {
      case SortTypes.DATE:
        filteredFields.sort((a, b) =>
          cmp(a.modifiedAt, b.modifiedAt, reverseSort, a.label, b.label)
        );
        break;
      case SortTypes.NAME:
        filteredFields.sort((a, b) => cmp(a.label, b.label, reverseSort));
        break;
      case SortTypes.DOC_COUNT:
        filteredFields.sort((a, b) => cmp(a.documentCount, b.documentCount, reverseSort));
        break;
    }
  }

  public async createMetadataField(result: FieldResult) {
    if (!result) {
      return null;
    }
    const groupId = this.groupService.getSelectedGroupId();

    const metadata: FieldMetadata = {
      filterable: result.filterable,
      redacted: result.containsPII,
      rating: result.rating,
    };

    if (result.type === 'calculated') {
      metadata.calculationType = result.calculationType;
      metadata.calculationDefinition = JSON.parse(result.calculationDefinition);
    }

    try {
      const res = await this.createField(
        groupId,
        result.name,
        result.type,
        null,
        metadata,
        result.alias,
        result.includedFields
      );

      this.alertService.showToast(
        this.translocoService.translate('field-list.field-created-successfully', {
          name: result.name,
        })
      );
      return res;
    } catch (err) {
      this.alertService.showErrorToast(
        this.translocoService.translate('field-list.field-created-error', {
          name: result.name,
        }),
        err
      );
    }
  }

  private getDefaultSideLoadForMetadatas() {
    return {
      sideloads: FieldService.defaultSideLoadForMetaDatas.map((fieldName) => fieldName).join(','),
    };
  }

  public setSelectedFilterOptions(selectedFilterOptions: SelectedFilterType[]) {
    this.selectedFilterOptions = selectedFilterOptions;
  }

  public getSelectedFilterOptions(): SelectedFilterType[] {
    if (this.selectedFilterOptions.length < 1) {
      for (const type of FieldConstants.FILTERTYPES) {
        this.selectedFilterOptions.push({ id: type, label: '', data: [], selected: [] });
      }
    }
    return this.selectedFilterOptions;
  }

  public static fillFieldDisplayNameForWidgets(options) {
    switch (options.type) {
      case 'bigNumber':
        if (options.useFieldDisplayNameOne) {
          options.metadataDisplayNameOne = FieldService.getFieldLongName(options.analyticItem);
        }

        if (options.useFieldDisplayNameTwo && options.analyticItemTwo) {
          options.metadataDisplayNameTwo = FieldService.getFieldLongName(options.analyticItemTwo);
        }
        break;
      case 'grid':
        if (options.useDisplayName) {
          options.breakdownLabel = FieldService.getFieldLongName(options.breakdownField);
        }
        for (const f of options.numericMetadataFields) {
          if (f.useDisplayName) {
            f.label = FieldService.getFieldLongName(f.fieldId);
          }
        }
        break;
      case 'numericMetadata':
        for (const f of options.numericMetadataFields) {
          if (f.useDisplayName) {
            f.label = FieldService.getFieldLongName(f.fieldId);
          }
        }
        break;
      case 'documents':
        if (!options.useFieldNames) {
          break;
        }

        if (!options.columnDisplayLabels) {
          options.columnDisplayLabels = {};
        }

        for (const [key, value] of Object.entries(options.useFieldNames)) {
          if (value) {
            options.columnDisplayLabels[key] = FieldService.getFieldLongName(key.split('.')[1]);
          }
        }
    }
  }
}

interface FieldMap {
  [key: string]: Field;
}

function createFieldMap(fieldListResult: StorageListResult<Field>): any {
  const item: FieldMap = {};
  for (const field of fieldListResult.items) {
    item[field.fieldName] = field;
  }
  return item;
}

function cmp(a: any, b: any, reverse: boolean = false, a2?: string, b2?: string): number {
  if (a === b) {
    return 0;
  }

  let order = 0;
  if (typeof a === 'string') {
    order = a.localeCompare(b);
  } else {
    [a, b].forEach((d) => d instanceof Date && d.setSeconds(0, 0));

    if (a > b) {
      order = 1;
    } else if (a < b) {
      order = -1;
    }

    //Secondary sort alphabetically (always A-Z)
    if (order === 0 && a2) {
      return (order = a2.localeCompare(b2));
    }
  }

  if (reverse) {
    order *= -1;
  }
  return order;
}

export const isReservedPrefixValidator: ValidatorFn = (
  control: UntypedFormControl
): ValidationErrors | null => {
  if (FieldService.isCustomField(control.value)) {
    return { reserved: true };
  }
};
